import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CircleCheck, FileText, Lock, MessageCircle, Receipt, Send, Wallet } from "lucide-react";
import { useMedia } from "@/hooks/use-media";
import { KpiTile } from "@/components/ui/kpi-tile";
import { SegmentedButtons } from "@/components/ui/segmented";
import { StatusBadge } from "@/components/ui/status-badge";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/ui/states";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { eur } from "@/lib/formato";
import {
  METODI_INCASSO,
  RATE,
  STATI_FATTURA,
  STATI_RATA,
  TONO,
  centesimiDaApi,
  centesimiDaTesto,
  centesimiInApi,
  centesimiInCampo,
  dataLunga,
  linkPromemoria,
  messaggioErrore,
  oggiRoma,
  quandoRata,
} from "@/lib/soldi";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/common/PageHeader";
import CampoData from "@/components/common/CampoData";
import { nuovoId } from "@/components/crm/FattoFuori";

/* Fatture e incassi: dell'amministrazione e del titolare. Il permesso vero sta
   nel database (RLS su incassi e fatture): qui, per il venditore, la pagina dice
   di chi è e non chiede niente al server. */

const SCHEDE = [
  { id: "incassi", label: "Incassi" },
  { id: "fatture", label: "Fatture" },
];

function Errore({ children, onRiprova }) {
  return (
    <ErrorState title="Non riesco a leggere i dati" actions={onRiprova ? <Button onClick={onRiprova}>Riprova</Button> : null}>
      {children} È solo una lettura: non si è perso niente.
    </ErrorState>
  );
}

const PASSO = 10;
// Vera solo perché il dialogo manda la chiave dell'incasso (c39: `id` scelto all'apertura) e il server la rispetta.
// Se la chiave non è su main alla finestra, questa riga si svuota e la frase esce.
const FRASE_UNA_SOLA_VOLTA = " Se l'incasso era già arrivato, non verrà segnato due volte.";
const GIORNI_PROSSIMI = 10;

/* Fra quanti giorni scade una rata (negativo = già scaduta). Date a mezzogiorno: niente scherzi di fuso. */
const giorniA = (iso, oggi) => Math.round((new Date(`${iso}T12:00:00`) - new Date(`${oggi}T12:00:00`)) / 86400000);

/* ── Incassi ──────────────────────────────────────────────────────────────── */

export function IncassoDialog({ rata, onOpenChange, onSegnato, onErrore }) {
  const [importo, setImporto] = useState("");
  const [data, setData] = useState(oggiRoma());
  const [metodo, setMetodo] = useState("bonifico");
  const [riferimento, setRiferimento] = useState("");
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState(null);
  // La chiave dell'incasso: una per apertura del dialogo, la stessa a ogni nuovo tentativo, buttata alla chiusura.
  const chiave = useRef(null);

  useEffect(() => {
    chiave.current = rata ? nuovoId() : null;
    setErrore(null);
    if (!rata) return;
    setImporto(centesimiInCampo(centesimiDaApi(rata.residuo)));
    setData(oggiRoma());
    setMetodo("bonifico");
    setRiferimento("");
  }, [rata]);

  const cent = centesimiDaTesto(importo);
  const residuo = rata ? centesimiDaApi(rata.residuo) : 0;
  // Dopo «Segna l'incasso» la pagina chiude il dialogo (rata → null) mentre l'importo scritto è
  // ancora lì: senza questa guardia qui si leggeva `rata.residuo` su null e la pagina cadeva
  // (in produzione, bianca) a ogni incasso segnato. Trovato nel giro intero del 25-09.
  const problema = !rata
    ? null
    : cent === null || cent <= 0
      ? "Scrivi l'importo incassato."
      : cent > residuo
        ? `Su questa rata restano ${eur(rata.residuo)}.`
        : data > oggiRoma()
          ? "Un incasso non può avere una data futura."
          : null;

  const segna = async (e) => {
    e.preventDefault();
    if (problema) return;
    setBusy(true);
    try {
      const r = await api.post("/incassi", {
        id: chiave.current,
        rata_id: rata.rata_id,
        importo: centesimiInApi(cent),
        data_incasso: data,
        metodo,
        riferimento: riferimento.trim() || null,
      });
      toast.success(`Incasso di ${eur(centesimiInApi(cent))} segnato`);
      onSegnato(r.data);
    } catch (err) {
      toast.error(messaggioErrore(err));
      setErrore(messaggioErrore(err));
      onErrore();  // la risposta può essersi persa dopo il salvataggio: la tabella si rilegge, così chi riapre vede il residuo vero
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(rata)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="incasso-modal">
        {rata ? (
          <form onSubmit={segna} className="grid gap-card">
            <DialogHeader>
              <DialogTitle>Segna incasso</DialogTitle>
              <DialogDescription>
                {rata.cliente} · {rata.causale} · contratto n. {rata.numero}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-card">
              <div className="grid gap-inside">
                <Label htmlFor="incasso-importo">Importo incassato</Label>
                <Input
                  id="incasso-importo"
                  inputMode="decimal"
                  autoComplete="off"
                  value={importo}
                  onChange={(e) => setImporto(e.target.value)}
                  data-testid="incasso-importo"
                />
                <p className="text-small text-ink-2">Restano {eur(rata.residuo)} su {eur(rata.importo)}.</p>
              </div>
              <div className="grid grid-cols-[minmax(0,1fr)] gap-card sm:grid-cols-2 sm:gap-group">
                <div className="grid gap-inside">
                  <Label htmlFor="incasso-data">Data dell'incasso</Label>
                  <CampoData id="incasso-data" value={data} max={oggiRoma()} onChange={(e) => setData(e.target.value)} testId="incasso-data" />
                </div>
                <div className="grid gap-inside">
                  <Label htmlFor="incasso-metodo">Come</Label>
                  <Select value={metodo} onValueChange={setMetodo}>
                    <SelectTrigger id="incasso-metodo" data-testid="incasso-metodo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METODI_INCASSO.map((m) => (
                        <SelectItem key={m.codice} value={m.codice}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-inside">
                <Label htmlFor="incasso-rif">
                  Riferimento <span className="font-normal text-ink-2">(facoltativo: CRO del bonifico, numero dell'assegno)</span>
                </Label>
                <Input id="incasso-rif" autoComplete="off" value={riferimento} onChange={(e) => setRiferimento(e.target.value)} />
              </div>
            </div>
            {problema && importo ? <p className="text-warning-ink">{problema}</p> : null}
            {errore ? (
              <div role="alert" className="rounded-md border border-danger-line bg-danger-bg p-3 text-danger-ink" data-testid="incasso-errore">
                <p className="font-semibold">Non è arrivata la conferma del salvataggio</p>
                <p>
                  {errore}. Quello che hai scritto è ancora qui: riprova.{FRASE_UNA_SOLA_VOLTA}
                </p>
              </div>
            ) : null}
            <DialogFooter className="gap-inside">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={Boolean(problema) || busy} data-testid="incasso-salva">
                {busy ? "Salvo…" : errore ? "Riprova" : "Segna l'incasso"}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function RataRiga({ r, largo, onSegna }) {
  const link = r.stato === "in_ritardo" ? linkPromemoria(r, { eur }) : null;
  const tono = TONO[STATI_RATA[r.stato]?.pill || "neutro"];
  const quando = <StatusBadge tone={tono}>{quandoRata(r)}</StatusBadge>;
  const sotto = (
    <>
      {r.causale} · contratto n. {r.numero}
      {centesimiDaApi(r.incassato) > 0 ? ` · già incassati ${eur(r.incassato)}` : ""}
    </>
  );
  const azioni = (
    <div className={cn("flex items-center gap-2", largo ? "justify-end" : "")}>
      {link ? (
        <Button asChild variant="outline" size="icon" aria-label={`Promemoria su WhatsApp a ${r.cliente}`}>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            title="Promemoria su WhatsApp: lo mandi tu, il testo è già scritto"
            data-testid={`promemoria-${r.rata_id}`}
          >
            <MessageCircle />
          </a>
        </Button>
      ) : null}
      <Button variant="outline" size="sm" onClick={() => onSegna(r)} data-testid={`segna-${r.rata_id}`}>
        <Wallet />
        Segna incasso
      </Button>
    </div>
  );
  const nome = (
    <Link to={`/contratti?c=${r.contratto_id}`} className="block min-h-6 truncate font-semibold text-ink-strong hover:underline max-md:py-3">
      {r.cliente}
    </Link>
  );
  if (!largo) {
    return (
      <li data-testid={`rata-${r.rata_id}`} className="flex flex-col gap-3 border-b border-line-subtle px-4 py-3.5 last:border-b-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {nome}
            <p className="text-small text-ink-2">{sotto}</p>
          </div>
          <span className="shrink-0 font-bold tabular-nums text-ink-strong">{eur(r.residuo)}</span>
        </div>
        <div>{quando}</div>
        {azioni}
      </li>
    );
  }
  return (
    <div
      role="row"
      data-testid={`rata-${r.rata_id}`}
      className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.4fr)_120px_216px] items-center gap-4 border-b border-line-subtle px-4 py-3 last:border-b-0"
    >
      <div role="cell" className="min-w-0">
        {nome}
        <p className="truncate text-small text-ink-2">{sotto}</p>
      </div>
      <div role="cell">{quando}</div>
      <span role="cell" className="whitespace-nowrap text-right font-bold tabular-nums text-ink-strong">{eur(r.residuo)}</span>
      <div role="cell">{azioni}</div>
    </div>
  );
}

function SchedaIncassi() {
  const [q, setQ] = useState(null);
  const [errore, setErrore] = useState(null);
  const [rata, setRata] = useState(null);
  const [togli, setTogli] = useState(null);
  const [quante, setQuante] = useState(PASSO);
  const [filtro, setFiltro] = useState("tutte");
  const largo = useMedia("(min-width: 768px)");

  const carica = useCallback(() => {
    api
      .get("/incassi")
      .then((r) => {
        setQ(r.data);
        setErrore(null);
      })
      .catch((e) => setErrore(messaggioErrore(e)));
  }, []);
  useEffect(carica, [carica]);

  const confermaTogli = async () => {
    const id = togli?.id;
    setTogli(null);
    try {
      const r = await api.delete(`/incassi/${id}`);
      setQ(r.data);
      toast.success("Incasso tolto: resta scritto nel registro");
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  if (errore) return <Errore onRiprova={carica}>{errore}</Errore>;
  if (!q) return <ListSkeleton rows={5} label="Carico gli incassi…" />;

  const { riepilogo, righe, ultimi_incassi: ultimi } = q;
  const oggi = q.oggi || oggiRoma();
  // Cosa scade nei prossimi 10 giorni: rate non ancora in ritardo con una scadenza vera.
  const presto = righe.filter((r) => r.stato !== "in_ritardo" && r.scadenza && giorniA(r.scadenza, oggi) >= 0 && giorniA(r.scadenza, oggi) <= GIORNI_PROSSIMI);
  const prestoTot = presto.reduce((s, r) => s + (centesimiDaApi(r.residuo) || 0), 0);
  const inRitardo = righe.filter((r) => r.stato === "in_ritardo");
  const elenco = filtro === "ritardo" ? inRitardo : filtro === "presto" ? presto : righe;
  const mostrate = elenco.slice(0, quante);
  const intestazione = "grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.4fr)_120px_216px] items-center gap-4 border-b border-line bg-surface-2 px-4 py-2.5 text-small font-semibold text-ink-2";
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="pannello-incassi">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-group sm:grid-cols-2 lg:grid-cols-4">
        <div data-testid="cifra-incassato">
          <KpiTile
            label="Incassato questo mese"
            value={centesimiDaApi(riepilogo.incassato_mese) / 100}
            detail={riepilogo.incassi_mese ? `${riepilogo.incassi_mese} ${riepilogo.incassi_mese === 1 ? "incasso segnato" : "incassi segnati"}` : "Nessun incasso segnato"}
          />
        </div>
        <div data-testid="cifra-da-incassare">
          <KpiTile label="Da incassare" value={centesimiDaApi(riepilogo.da_incassare) / 100} detail="Rate aperte dei contratti firmati" />
        </div>
        <div data-testid="cifra-in-ritardo">
          <KpiTile
            label="In ritardo"
            value={centesimiDaApi(riepilogo.in_ritardo) / 100}
            tone={riepilogo.rate_in_ritardo ? "danger" : "default"}
            detail={riepilogo.rate_in_ritardo ? `${riepilogo.rate_in_ritardo} ${riepilogo.rate_in_ritardo === 1 ? "rata oltre la scadenza" : "rate oltre la scadenza"}` : "Nessuno in ritardo"}
          />
        </div>
        <div data-testid="cifra-presto">
          <KpiTile
            label={`Scadono entro ${GIORNI_PROSSIMI} giorni`}
            value={prestoTot / 100}
            detail={presto.length ? `${presto.length} ${presto.length === 1 ? "rata" : "rate"}` : "Nessuna rata in scadenza"}
          />
        </div>
      </div>

      {righe.length === 0 ? (
        <div data-testid="incassi-vuoto">
          <EmptyState icon={CircleCheck} tone="success" title="Nessuno deve pagare">
            Qui compaiono le rate dei contratti firmati che aspettano ancora un incasso, a partire da chi è in ritardo.
          </EmptyState>
        </div>
      ) : (
        <section className="overflow-hidden rounded-xl border border-line bg-surface" aria-labelledby="chi-deve-pagare">
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-line px-4 py-3.5">
            <div>
              <h2 id="chi-deve-pagare" className="font-display text-h3 text-ink-strong">Chi deve pagare</h2>
              <p className="text-ink-2">Prima chi è in ritardo, poi le scadenze più vicine.</p>
            </div>
            <SegmentedButtons
              aria-label="Quali rate"
              className="[&>button]:whitespace-nowrap"
              prefisso="incassi-filtro"
              valore={filtro}
              onCambia={(f) => { setFiltro(f); setQuante(PASSO); }}
              voci={[
                { id: "tutte", label: "Tutte" },
                { id: "ritardo", label: `In ritardo ${inRitardo.length}` },
                { id: "presto", label: `Entro ${GIORNI_PROSSIMI} giorni ${presto.length}` },
              ]}
            />
          </div>
          {elenco.length === 0 ? (
            <div className="px-4 py-2" data-testid="incassi-filtro-vuoto">
              <EmptyState icon={CircleCheck} tone="success" title={filtro === "ritardo" ? "Nessuno è in ritardo" : "Nessuna rata in scadenza"}>
                {filtro === "ritardo"
                  ? "Tutte le rate scadute sono state incassate."
                  : `Nessuna rata scade nei prossimi ${GIORNI_PROSSIMI} giorni.`}
              </EmptyState>
            </div>
          ) : largo ? (
            <div role="table" aria-label="Chi deve pagare" data-testid="incassi-tabella">
              <div role="row" className={intestazione}>
                <span role="columnheader">Cliente e rata</span>
                <span role="columnheader">Quando</span>
                <span role="columnheader" className="text-right">Da incassare</span>
                <span role="columnheader" className="sr-only">Azioni</span>
              </div>
              <div role="rowgroup">
                {mostrate.map((r) => (
                  <RataRiga key={r.rata_id} r={r} largo onSegna={setRata} />
                ))}
              </div>
            </div>
          ) : (
            <ul data-testid="incassi-tabella">
              {mostrate.map((r) => (
                <RataRiga key={r.rata_id} r={r} largo={false} onSegna={setRata} />
              ))}
            </ul>
          )}
          {elenco.length ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3 text-small text-ink-2">
              <span className="tabular-nums">{mostrate.length} di {elenco.length} rate</span>
              {elenco.length > mostrate.length ? (
                <Button variant="outline" onClick={() => setQuante(elenco.length)} data-testid="incassi-tutte">
                  Mostra tutte
                </Button>
              ) : null}
            </div>
          ) : null}
        </section>
      )}

      {ultimi.length ? (
        <section className="rounded-xl border border-line bg-surface p-4 md:p-5" data-testid="ultimi-incassi">
          <h2 className="font-display text-h3 text-ink-strong">Ultimi incassi del mese</h2>
          <ul className="mt-3 divide-y divide-line-subtle">
            {ultimi.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-inside py-2.5">
                <div className="min-w-0">
                  <p className="text-ink">
                    <strong className="tabular-nums">{eur(i.importo)}</strong> · {i.cliente || "—"}
                  </p>
                  <p className="text-small text-ink-2">
                    Incassato il {dataLunga(i.data_incasso)}
                    {i.metodo ? `, ${METODI_INCASSO.find((m) => m.codice === i.metodo)?.label.toLowerCase() || i.metodo}` : ""} · segnato da {i.segnato_da}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setTogli(i)} data-testid={`togli-${i.id}`}>
                  Togli
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <IncassoDialog
        rata={rata}
        onOpenChange={(v) => !v && setRata(null)}
        onErrore={carica}
        onSegnato={(nuovo) => {
          setRata(null);
          setQ(nuovo);
        }}
      />
      <AlertDialog open={Boolean(togli)} onOpenChange={(v) => !v && setTogli(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Togliere l'incasso di {togli ? eur(togli.importo) : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              Solo se è stato segnato per sbaglio. La rata torna da incassare, e nel registro resta chi l'ha tolto e quando.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Lascialo</AlertDialogCancel>
            <AlertDialogAction onClick={confermaTogli} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Togli l'incasso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ── Fatture ──────────────────────────────────────────────────────────────── */

function AvvisoCollegamento({ fic }) {
  if (!fic) return null;
  if (fic.stato === "rinnovo_fallito" || fic.stato === "errore") {
    return (
      <div role="alert" className="flex gap-inside rounded-xl border border-danger-line bg-danger-bg px-4 py-3.5 text-danger-ink" data-testid="fic-avviso">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
        <p>{fic.avviso || "Il collegamento a Fatture in Cloud non funziona: va rifatto da Impostazioni."}</p>
      </div>
    );
  }
  if (!fic.attivo) {
    return (
      <div className="flex gap-inside rounded-xl border border-line bg-surface-2 px-4 py-3.5 text-ink-2" data-testid="fic-spento">
        <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
        <p>
          Il collegamento a Fatture in Cloud è spento. Le fatture si preparano qui dal piano di pagamento, coi numeri già giusti;
          finché non si accende, si emettono su Fatture in Cloud a mano.
        </p>
      </div>
    );
  }
  return null;
}

function SchedaFatture() {
  const [f, setF] = useState(null);
  const [errore, setErrore] = useState(null);
  const [busy, setBusy] = useState(null);
  const largo = useMedia("(min-width: 768px)");

  const carica = useCallback(() => {
    api
      .get("/fatture")
      .then((r) => {
        setF(r.data);
        setErrore(null);
      })
      .catch((e) => setErrore(messaggioErrore(e)));
  }, []);
  useEffect(carica, [carica]);

  const esegui = async (chiave, chiamata, messaggio) => {
    setBusy(chiave);
    try {
      const r = await chiamata();
      setF(r.data);
      if (messaggio) toast.success(messaggio);
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(null);
    }
  };

  if (errore) return <Errore onRiprova={carica}>{errore}</Errore>;
  if (!f) return <ListSkeleton rows={4} label="Carico le fatture…" />;

  const acceso = f.fic?.attivo && f.fic?.stato === "collegato";
  const riga = "flex flex-wrap items-center justify-between gap-3 border-b border-line-subtle px-4 py-3.5 last:border-b-0";
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="pannello-fatture">
      <AvvisoCollegamento fic={f.fic} />

      {f.da_preparare.length ? (
        <section className="overflow-hidden rounded-xl border border-line bg-surface" data-testid="fatture-da-preparare" aria-labelledby="fatture-da-fatturare">
          <div className="border-b border-line px-4 py-3.5">
            <h2 id="fatture-da-fatturare" className="font-display text-h3 text-ink-strong">Da fatturare</h2>
            <p className="text-ink-2">Le rate dei contratti firmati che non hanno ancora la loro fattura.</p>
          </div>
          <ul>
            {f.da_preparare.map((r) => (
              <li key={r.rata_id} className={riga}>
                <div className="min-w-0 flex-1 basis-56">
                  <p className="truncate font-semibold text-ink-strong">{r.cliente}</p>
                  <p className="text-small text-ink-2">
                    {r.causale || RATE[r.tipo]} · contratto n. {r.numero}
                    {r.scadenza ? ` · scade il ${dataLunga(r.scadenza)}` : ""}
                  </p>
                </div>
                <span className="font-bold tabular-nums text-ink-strong">{eur(r.importo)}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy === r.rata_id}
                  onClick={() => esegui(r.rata_id, () => api.post("/fatture/da-rata", { rata_id: r.rata_id }), "Fattura preparata")}
                  data-testid={`prepara-${r.rata_id}`}
                >
                  <FileText />
                  Prepara la fattura
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {f.fatture.length === 0 ? (
        <div data-testid="fatture-vuoto">
          <EmptyState icon={Receipt} title="Nessuna fattura ancora">
            La fattura d'acconto si prepara dal piano di pagamento di un contratto firmato: importo, IVA e cliente entrano da soli.
          </EmptyState>
        </div>
      ) : (
        <section className="overflow-hidden rounded-xl border border-line bg-surface" data-testid="fatture-tabella" aria-label="Fatture">
          <ul>
            {f.fatture.map((x) => {
              const s = STATI_FATTURA[x.stato] || STATI_FATTURA.emessa;
              return (
                <li key={x.id} data-testid={`fattura-${x.id}`} className={riga}>
                  <div className="min-w-0 flex-1 basis-64">
                    <p className="truncate font-semibold text-ink-strong">{x.cliente || "—"}</p>
                    <p className="text-small text-ink-2">
                      {[x.numero ? `n. ${x.numero}` : null, x.causale, x.data_emissione ? `del ${dataLunga(x.data_emissione)}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <p className="text-small text-ink-2">
                      Imponibile {eur(x.imponibile)} · IVA {eur(x.iva)}
                    </p>
                    {x.fic_errore ? <p className="mt-1 text-small text-danger-ink">{x.fic_errore}</p> : null}
                  </div>
                  <StatusBadge tone={TONO[s.pill]}>{s.label}</StatusBadge>
                  <span className="min-w-24 text-right font-bold tabular-nums text-ink-strong">{eur(x.totale)}</span>
                  <div className={cn("flex flex-wrap items-center gap-2", largo ? "justify-end" : "w-full")}>
                    {x.stato === "da_emettere" ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!acceso || busy === x.id}
                          title={acceso ? undefined : "Il collegamento a Fatture in Cloud è spento"}
                          onClick={() => esegui(x.id, () => api.post(`/fatture/${x.id}/invia`), "Fattura inviata a Fatture in Cloud")}
                          data-testid={`invia-${x.id}`}
                        >
                          <Send />
                          Invia
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy === x.id}
                          onClick={() => esegui(x.id, () => api.post(`/fatture/${x.id}/annulla`), "Fattura annullata")}
                          data-testid={`annulla-${x.id}`}
                        >
                          Annulla
                        </Button>
                      </>
                    ) : null}
                    {x.stato === "emessa" && x.fic_id && acceso ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy === x.id}
                        onClick={() => esegui(x.id, () => api.post(`/fatture/${x.id}/sincronizza`), "Stato aggiornato da Fatture in Cloud")}
                      >
                        Aggiorna lo stato
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

/* ── La pagina ────────────────────────────────────────────────────────────── */

export default function FattureIncassiPage() {
  const { profile } = useAuth();
  const ruolo = profile?.ruolo;
  const [params, setParams] = useSearchParams();
  const scheda = params.get("scheda") === "fatture" ? "fatture" : "incassi";
  const ammesso = ruolo === "titolare" || ruolo === "amministrazione";

  return (
    <section data-testid="page-fatture-incassi" className="grid grid-cols-[minmax(0,1fr)] gap-card">
      <PageHeader
        title="Fatture e incassi"
        description="Chi deve ancora pagare, e da quanto. Gli incassi si segnano a mano: il registro tiene chi e quando."
      />
      {!ruolo ? (
        <ListSkeleton rows={3} label="Carico la pagina…" />
      ) : !ammesso ? (
        <div data-testid="fatture-negata">
          <EmptyState
            icon={Lock}
            title="Questa pagina è dell'amministrazione"
            actions={
              <Button asChild variant="outline">
                <Link to="/contratti">Vai ai contratti</Link>
              </Button>
            }
          >
            Fatture e incassi li vedono il titolare e l'amministrazione. I tuoi contratti, col loro piano di pagamento, sono in Contratti.
          </EmptyState>
        </div>
      ) : (
        <>
          <Tabs
            value={scheda}
            onValueChange={(v) => setParams(v === "incassi" ? {} : { scheda: v }, { replace: true })}
            className="min-w-0"
          >
            <TabsList data-testid="fatture-schede">
              {SCHEDE.map((s) => (
                <TabsTrigger key={s.id} value={s.id} className="min-h-11 sm:min-h-7" data-testid={`scheda-${s.id}`}>
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {scheda === "incassi" ? <SchedaIncassi /> : <SchedaFatture />}
        </>
      )}
    </section>
  );
}
